import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ModalInfoType } from '@components/molecules/modal/Modal'

import type {
	PaymentStateType,
	ApiGetDataStatus,
	PaymentCustomerInfoType,
	PaymentDeliveryType,
	PaymentShippingType,
	PaymentCardType,
	PaymentCostType,
	CheckoutQuoteType,
	CheckoutQuoteProblemType,
} from '@redux/types'
import { ShippingCostType } from '@schemas'

export const unsupportedPayPalCountries = [
	'AF', // 아프가니스탄
	'BD', // 방글라데시
	'CM', // 카메룬
	'CF', // 중앙아프리카공화국
	'CI', // 코트디부아르
	'CU', // 쿠바
	'IR', // 이란
	'IQ', // 이라크
	'KP', // 북한
	'LB', // 레바논
	'LR', // 라이베리아
	'LY', // 리비아
	'MM', // 미얀마
	'PK', // 파키스탄
	'SD', // 수단
	'SY', // 시리아
	'ZW', // 짐바브웨
	'BY', // 벨라루스
	'BO', // 볼리비아
	'ET', // 에티오피아
	'GH', // 가나
	'NE', // 니제르
	'NG', // 나이지리아
	'LK', // 스리랑카
	'TG', // 토고
	'TM', // 투르크메니스탄
	'UA', // 우크라이나
	'UZ', // 우즈베키스탄
	'VE', // 베네수엘라
	'VN', // 베트남
	'YE', // 예멘
	'KR', // 한국
]

// initial state
const initialState: PaymentStateType = {
	triggerInitPaymentPage: false,
	triggerUsdPayNow: false,
	triggerKrwPayNow: false,
	triggerCouponApply: false,
	triggerGetShippingCost: false,
	triggerResetPaymentData: false,
	triggerQuote: false,

	paymentStatus: 'init',
	quote: null,
	quoteStatus: 'init',
	quoteProblems: [],
	priceChanges: { currency: 'USD', increased: [], decreased: [] },
	tryToPayment: {
		cartList: [],
	},

	showModal: false,
	modalInfo: {
		target: '',
		title: '',
	},

	// shipping cost
	shippingCostList: [],
	shippingCostStatus: 'init',

	// payment data
	paymentMethod: '',
	paymentCustomerInfo: {
		givenName: '',
		familyName: '',
		email: '',
		phoneCountryCode: '',
		phoneNumber: '',
	},
	paymentDelivery: {
		countryName: '',
		countryIsoCode: '',
		stateName: '',
		stateIsoCode: '',
		city: '',
		street1: '',
		street2: '',
		zip: '',
	},
	paymentShipping: {
		companyName: '',
		deliveryOption: '',
		usd: 0,
		deliveryDays: 0,
		boxType: 'A',
		rateType: '',
	},
	paymentCard: {
		cardNumber: '',
		expiredMonth: '',
		expiredYear: '',
		cvc: '',
		cardName: '',
	},
	paymentCost: {
		totalUsd: 0,
		vatUsd: 0,
		shippingUsd: 0,
		discountedUsd: 0,
		discountedKrw: 0,
		paidUsd: 0,
		couponCode: '',
		currency: 'USD',
		subtotalAmount: 0,
		discountAmount: 0,
		shippingAmount: 0,
		paidAmount: 0,
	},

	// payment validation
	paymentCouponErrorMessage: '',
	paymentErrorMessages: {
		customer: {
			givenName: '',
			familyName: '',
			email: '',
			phoneCountryCode: '',
			phoneNumber: '',
		},
		delivery: {
			countryName: '',
			states: '',
			city: '',
			street1: '',
			zip: '',
		},
		shipping: {
			companyName: '',
		},
		card: {
			cardNumber: '',
			expiredMonth: '',
			expiredYear: '',
			cvc: '',
			cardName: '',
		},
		/*
			**마지막 검증의 결과**다. 검증은 Pay now(카드) · PayPal 버튼을 누를 때만 돈다.
			그래서 초기값은 `true` — 아직 실패한 적이 없다는 뜻이다. `false` 로 두면 화면을
			열자마자 "빠진 항목을 확인하라"는 경고가 서 있는다(2026-09-22 수정).
		*/
		isSuccess: true,
	},
	paymentSuccess: {
		orderId: '',
		orderDetail: '',
		address: '',
		email: '',
		phoneNumber: '',
		currency: '',
		paidAmount: 0,
		confirming: false,
		cancelToken: '',
	},
}

const slice = createSlice({
	name: 'payment',
	initialState,
	reducers: {
		TRIGGER_INIT_PAYMENT_PAGE: (
			state: PaymentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInitPaymentPage = action.payload
		},
		TRIGGER_USD_PAY_NOW: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerUsdPayNow = action.payload
		},
		TRIGGER_KRW_PAY_NOW: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerKrwPayNow = action.payload
		},
		TRIGGER_COUPON_APPLY: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerCouponApply = action.payload
		},
		TRIGGER_GET_SHIPPING_COST: (
			state: PaymentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetShippingCost = action.payload
		},
		TRIGGER_RESET_PAYMENT_DATA: (
			state: PaymentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerResetPaymentData = action.payload
		},

		TRIGGER_QUOTE: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerQuote = action.payload
		},

		PAYMENT_STATUS: (
			state: PaymentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.paymentStatus = action.payload
		},
		QUOTE: (state: PaymentStateType, action: PayloadAction<CheckoutQuoteType | null>) => {
			state.quote = action.payload
		},
		QUOTE_STATUS: (state: PaymentStateType, action: PayloadAction<ApiGetDataStatus>) => {
			state.quoteStatus = action.payload
		},
		QUOTE_PROBLEMS: (
			state: PaymentStateType,
			action: PayloadAction<CheckoutQuoteProblemType[]>,
		) => {
			state.quoteProblems = action.payload
		},
		/** PriceChangeModal 의 결정 — 사가가 듣는다. payload = 체크된(살) 오른 줄의 시즌상품 ID */
		PRICE_DECISION: (state: PaymentStateType, action: PayloadAction<string[]>) => {},
		PRICE_CHANGES: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['priceChanges']>,
		) => {
			state.priceChanges = action.payload
		},
		TRY_TO_PAYMENT: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['tryToPayment']>,
		) => {
			state.tryToPayment = action.payload
		},

		SHOW_MODAL: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.showModal = action.payload
		},
		MODAL_INFO: (state: PaymentStateType, action: PayloadAction<ModalInfoType>) => {
			state.modalInfo = action.payload
		},

		// shipping cost
		SHIPPING_COST_LIST: (
			state: PaymentStateType,
			action: PayloadAction<ShippingCostType[]>,
		) => {
			state.shippingCostList = action.payload
		},
		SHIPPING_COST_STATUS: (
			state: PaymentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.shippingCostStatus = action.payload
		},

		// payment data
		PAYMENT_METHOD: (state: PaymentStateType, action: PayloadAction<string>) => {
			state.paymentMethod = action.payload
		},
		PAYMENT_CUSTOMER_INFO: (
			state: PaymentStateType,
			action: PayloadAction<PaymentCustomerInfoType>,
		) => {
			state.paymentCustomerInfo = action.payload
		},
		PAYMENT_DELIVERY: (
			state: PaymentStateType,
			action: PayloadAction<PaymentDeliveryType>,
		) => {
			state.paymentDelivery = action.payload
		},
		PAYMENT_SHIPPING: (
			state: PaymentStateType,
			action: PayloadAction<PaymentShippingType>,
		) => {
			state.paymentShipping = action.payload
		},
		PAYMENT_CARD: (state: PaymentStateType, action: PayloadAction<PaymentCardType>) => {
			state.paymentCard = action.payload
		},
		PAYMENT_COST: (state: PaymentStateType, action: PayloadAction<PaymentCostType>) => {
			state.paymentCost = action.payload
		},

		PAYMENT_COUPON_ERROR_MESSAGE: (
			state: PaymentStateType,
			action: PayloadAction<string>,
		) => {
			state.paymentCouponErrorMessage = action.payload
		},
		PAYMENT_ERROR_MESSAGES: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['paymentErrorMessages']>,
		) => {
			state.paymentErrorMessages = action.payload
		},
		/**
		 * 지난 시도의 검증 결과를 지운다. 결제 화면을 **열 때** 부른다 — 상태가 세션에 남아 있어
		 * (redux-persist) 한 번 실패한 뒤 다른 화면에 갔다 돌아오면 경고가 그대로 서 있었다.
		 */
		RESET_PAYMENT_ERROR_MESSAGES: (state: PaymentStateType) => {
			state.paymentErrorMessages = initialState.paymentErrorMessages
		},
		PAYMENT_SUCCESS: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['paymentSuccess']>,
		) => {
			state.paymentSuccess = action.payload
		},
	},
})

export const paymentActions = slice.actions
export default slice.reducer
